import React, { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { Link } from "react-router-dom";
import MenuBar from "../../components/MenuBar";
import HeaderBar from "../../components/HeaderBar";
import { getCctvSettings, updateCctvSettings } from "../../services/cctvApi";

function defaultSettings() {
  return {
    gatewayPublicUrl: "",
    functionsGatewayUrl: "",
    hlsBasePath: "/",
    webrtcEnabled: true,
    maxConcurrentStreams: 50,
    healthCheckIntervalSec: 60,
  };
}

function isHomePublicHttpUrl(url) {
  const u = String(url || "").trim();
  if (!/^http:\/\//i.test(u)) return false;
  try {
    const host = new URL(u).hostname;
    const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(host);
    if (!m) return false;
    const parts = m.slice(1, 5).map(Number);
    if (parts.some((p) => p > 255)) return false;
    const [a, b] = parts;
    if (a === 10) return false;
    if (a === 172 && b >= 16 && b <= 31) return false;
    if (a === 192 && b === 168) return false;
    if (a === 127) return false;
    if (a === 100 && b >= 64 && b <= 127) return false;
    return true;
  } catch {
    return false;
  }
}

export default function CctvSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState(defaultSettings);
  const [urlWarning, setUrlWarning] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getCctvSettings();
        if (!cancelled) {
          const s = res?.data?.settings || {};
          setCfg({ ...defaultSettings(), ...s });
          setUrlWarning(s.gatewayUrlWarning || "");
        }
      } catch (e) {
        if (!cancelled) toast.error(e?.message || "Failed to load settings");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (isHomePublicHttpUrl(cfg.functionsGatewayUrl)) {
      toast.error(
        "Functions gateway URL cannot be http://YOUR_PUBLIC_IP — use an HTTPS Cloudflare tunnel or Tailscale (100.x.x.x)."
      );
      return;
    }
    if (isHomePublicHttpUrl(cfg.gatewayPublicUrl)) {
      toast.error("HLS URL cannot be http://YOUR_PUBLIC_IP — use an HTTPS tunnel or LAN IP.");
      return;
    }
    setSaving(true);
    try {
      await updateCctvSettings({ settings: cfg });
      setUrlWarning("");
      toast.success("CCTV settings saved");
    } catch (err) {
      toast.error(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar title="CCTV — Settings" />

      <div className="mx-auto max-w-2xl px-4 py-6">
        <p className="mb-4 text-sm text-slate-600">
          <strong>Gateway public URL</strong> is for browsers (HLS). <strong>Functions → gateway URL</strong> is for
          Firebase in the cloud to call your Docker gateway API (port 8090). RTSP stays on the server. See{" "}
          <code className="text-xs">cctv/docker-compose.yml</code>.
        </p>

        {!loading && !cfg.functionsGatewayUrl?.trim() && (
          <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
            <p className="font-semibold">Gateway URL not configured</p>
            <p className="mt-1 text-xs leading-relaxed">
              On the PC running cameras, install <strong>Docker Desktop</strong>, then in PowerShell from the project
              folder run:{" "}
              <code className="rounded bg-white px-1">npm run cctv:setup</code> (or{" "}
              <code className="rounded bg-white px-1">cctv\setup-gateway.ps1</code>). That starts Docker, opens two
              Cloudflare tunnels, and saves the URLs here automatically. Keep that window open while using CCTV live.
            </p>
          </div>
        )}

        {!loading && (urlWarning === "public_http_unreachable" || isHomePublicHttpUrl(cfg.functionsGatewayUrl)) && (
          <div className="mb-4 rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-950">
            <p className="font-semibold">This gateway URL will not work from Firebase</p>
            <p className="mt-1 text-xs">
              <code className="rounded bg-white px-1">http://YOUR_PUBLIC_IP:8090</code> (e.g. 49.47.194.223) is blocked
              for home broadband — use an <strong>HTTPS Cloudflare tunnel</strong> URL below instead.
            </p>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : (
          <form onSubmit={save} className="space-y-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-slate-700">Functions → gateway API URL (port 8090)</span>
              <input
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                placeholder="https://….trycloudflare.com (tunnel) or http://100.x.x.x:8090 (Tailscale)"
                value={cfg.functionsGatewayUrl}
                onChange={(e) => setCfg((c) => ({ ...c, functionsGatewayUrl: e.target.value }))}
              />
              <span className="mt-1 block text-xs text-slate-500">
                Must be reachable from <strong>Google Cloud</strong> (not 127.0.0.1). Home broadband usually{" "}
                <strong>cannot</strong> use <code className="rounded bg-slate-100 px-0.5">http://49.x.x.x:8090</code> — use
                a Cloudflare HTTPS tunnel or Tailscale. Stored in Firestore — no functions redeploy.
              </span>
            </label>

            <label className="block text-sm">
              <span className="mb-1 block font-medium text-slate-700">Gateway public URL (HLS host)</span>
              <input
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                placeholder="https://….trycloudflare.com (HLS tunnel) or http://192.168.1.10:8888"
                value={cfg.gatewayPublicUrl}
                onChange={(e) => setCfg((c) => ({ ...c, gatewayPublicUrl: e.target.value }))}
              />
              <span className="mt-1 block text-xs text-slate-500">Base URL clients use to play HLS (no trailing slash).</span>
            </label>

            <label className="block text-sm">
              <span className="mb-1 block font-medium text-slate-700">HLS base path</span>
              <input
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                value={cfg.hlsBasePath}
                onChange={(e) => setCfg((c) => ({ ...c, hlsBasePath: e.target.value }))}
              />
            </label>

            <label className="block text-sm">
              <span className="mb-1 block font-medium text-slate-700">Max concurrent streams</span>
              <input
                type="number"
                min={1}
                max={200}
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                value={cfg.maxConcurrentStreams}
                onChange={(e) => setCfg((c) => ({ ...c, maxConcurrentStreams: Number(e.target.value) || 50 }))}
              />
            </label>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!cfg.webrtcEnabled}
                onChange={(e) => setCfg((c) => ({ ...c, webrtcEnabled: e.target.checked }))}
              />
              <span>WebRTC enabled (when gateway supports WHEP)</span>
            </label>

            <label className="block text-sm">
              <span className="mb-1 block font-medium text-slate-700">Health check interval (seconds)</span>
              <input
                type="number"
                min={15}
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                value={cfg.healthCheckIntervalSec}
                onChange={(e) => setCfg((c) => ({ ...c, healthCheckIntervalSec: Number(e.target.value) || 60 }))}
              />
            </label>

            <div className="flex flex-wrap gap-2 pt-2">
              <button
                type="submit"
                disabled={saving}
                className="rounded bg-red-700 px-4 py-2 text-sm font-bold text-white hover:bg-red-600 disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save settings"}
              </button>
              <Link to="/cctv" className="rounded border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                Live view
              </Link>
              <Link
                to="/cctv/cameras"
                className="rounded border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cameras
              </Link>
            </div>
          </form>
        )}

        <div className="mt-6 rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm text-sky-950">
          <p className="font-semibold">Cannot reach your public IP from Firebase? (timeout / fetch failed)</p>
          <p className="mt-2 text-xs leading-relaxed">
            Many home connections block <strong>inbound</strong> traffic. Use <strong>Cloudflare quick tunnels</strong> (outbound only) — no router port-forwarding.
          </p>
          <ol className="mt-2 list-decimal space-y-2 pl-5 text-xs">
            <li>
              <strong>Get two HTTPS URLs</strong> (one for API port 8090, one for HLS port 8888). Pick one approach:
              <ul className="mt-1 list-disc space-y-1 pl-4">
                <li>
                  <strong>Docker:</strong> <code className="rounded bg-white px-1">cd cctv</code> →{" "}
                  <code className="rounded bg-white px-1">docker compose --profile tunnel up -d</code> → read logs:{" "}
                  <code className="rounded bg-white px-1">docker compose logs cloudflared-gateway</code> and{" "}
                  <code className="rounded bg-white px-1">cloudflared-hls</code> (look for{" "}
                  <code className="rounded bg-white px-1">trycloudflare.com</code>).
                </li>
                <li>
                  <strong>Windows + cloudflared only:</strong> install{" "}
                  <a
                    className="font-medium underline"
                    href="https://developers.cloudflare.com/cloudflare-one/connections/connect-networks/downloads/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    cloudflared
                  </a>
                  , run <code className="rounded bg-white px-1">docker compose up -d</code>, then in two terminals:{" "}
                  <code className="rounded bg-white px-1">cloudflared tunnel --url http://127.0.0.1:8090</code> and{" "}
                  <code className="rounded bg-white px-1">cloudflared tunnel --url http://127.0.0.1:8888</code> — copy each
                  printed <code className="rounded bg-white px-1">https://…trycloudflare.com</code>.
                </li>
              </ul>
            </li>
            <li>
              Paste the <strong>8090</strong> tunnel into <strong>Functions → gateway API URL</strong> (must be{" "}
              <code className="rounded bg-white px-1">https://</code>, no path).
            </li>
            <li>
              Paste the <strong>8888</strong> tunnel into <strong>Gateway public URL (HLS host)</strong> (no trailing slash).
            </li>
            <li>
              <strong>Save settings</strong>, then open CCTV live again. Remove or replace any old{" "}
              <code className="rounded bg-white px-1">http://49.x.x.x:8090</code> value — the cloud cannot reach most home
              public IPs.
            </li>
          </ol>
        </div>

        <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <p className="font-semibold">Server environment (Firebase Functions)</p>
          <ul className="mt-2 list-inside list-disc text-xs">
            <li>
              <code>CCTV_ENCRYPTION_KEY</code> — Secret Manager
            </li>
            <li>
              <code>CCTV_GATEWAY_SECRET</code> — must match <code>cctv/.env</code> on the gateway host
            </li>
            <li>
              Optional fallback: <code>CCTV_GATEWAY_URL</code> in <code>functions/.env</code> (deploy). Prefer{" "}
              <strong>Functions → gateway API URL</strong> above so you can change the URL without redeploying.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
